import React from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { css } from '@linaria/core'
import { colors } from '@fs/zion-ui'
import { Link } from '@fs/zion-router'
import { i18n } from '@fs/zion-locale'
import { useFeatureFlag } from '@fs/zion-flags'
import churchLinks from '../../components/ChurchLinks'
import CookiePreferences from '../../components/CookiePreferences'
import { buildUrl } from '../../footerHelperFunctions'
import AccessibilityLink from '../../components/AccessibilityLink'

const legalCss = css`
  color: ${colors.transparent.gray60};
  font-size: 12px;
  line-height: 1.33;
  text-align: center;

  @media (min-width: 444px) {
    text-align: start;
  }
`

const legalLinkCss = css`
  white-space: nowrap;

  &,
  &:visited {
    color: ${colors.gray90} !important;
    text-decoration-color: ${colors.gray90} !important;
  }
`
const linkContainerCss = css`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;

  @media (min-width: 444px) {
    justify-content: flex-start;
  }
`

export default function Text({ nonReligiousMode, external }) {
  const [t] = useTranslation()
  const year = new Date().getFullYear()

  const churchLink = churchLinks[i18n.language] ? churchLinks[i18n.language] : churchLinks.en
  const countryAndLangOptionsOn = useFeatureFlag('home_localePage').isOn

  const lineOne = [
    {
      langKey: 'fatFooter.terms-of-use',
      path: `/${i18n.language}/legal/terms`,
      linkName: 'ff_terms',
      text: 'FamilySearch ',
      linkText: 'Terms of Use',
    },
    {
      langKey: 'fatFooter.privacy-notice',
      path: `/${i18n.language}/legal/privacy`,
      linkName: 'ff_privacy',
      linkText: 'Privacy Notice',
    },
    {
      // langKey not used in translation file, needed for React key
      langKey: 'fatFooter.cookie-preferences',
      callback: true,
      tabIndex: '-1',
      linkName: 'ff_cookie_preferences',
    },
    {
      langKey: 'fatFooter.site-map',
      path: '/site-map',
      linkName: 'ff_site_map',
      linkText: 'Site Map',
    },
  ]

  if (countryAndLangOptionsOn) {
    lineOne.push({
      langKey: 'footer.country-and-language-options',
      path: `/${i18n.language}/locale`,
      linkName: 'ff_country_and_lang_options',
      linkText: 'Country and Language Options',
    })
  }

  return (
    <div className={legalCss}>
      <AccessibilityLink external={external} />
      <div className={linkContainerCss}>
        {lineOne.map((section, index) => (
          <div key={section.langKey}>
            {/* the only link that currently has a callback is the cookie preferences button */}
            {section.callback ? (
              <CookiePreferences includeButton updatedButton />
            ) : (
              <Trans
                i18nKey={section.langKey}
                values={section.value}
                defaults={`${section.text}<0>${section.linkText}</0>`}
                components={[
                  <Link
                    key={section.langKey}
                    external
                    to={buildUrl(section.path, external)}
                    className={legalLinkCss}
                    linkName={section.linkName}
                  >
                    {section.linkText}
                  </Link>,
                ]}
              />
            )}

            {index !== lineOne.length - 1 && <>&nbsp;|&nbsp;</>}
          </div>
        ))}
      </div>
      {t('footer.copyright-notice', '© {year} by Intellectual Reserve, Inc. All rights reserved.', { year })}{' '}
      <Trans i18nKey="footer.service-by-church.link">
        A service provided by
        {!nonReligiousMode ? (
          <Link to={churchLink} className={legalLinkCss} linkName="footer_service_provided_by_church" external>
            The Church of Jesus Christ of Latter-day Saints
          </Link>
        ) : (
          <span>The Church of Jesus Christ of Latter-day Saints</span>
        )}
      </Trans>
      .
    </div>
  )
}
